import { Fragment, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";
import type {
  PlaybackBreakdownRow,
  PlaybackMetrics,
} from "@shared/types/playbackAnalytics";
import { tokens } from "../../theme";
import { useFormat } from "../../i18n/format";
import { ChangeBadge, type Better } from "./charts";

/**
 * One breakdown as a table: sortable by any column, searchable, a change
 * column against the previous period, and a row click that drills in.
 */

export interface MetricColumn {
  key: keyof PlaybackMetrics;
  label: string;
  format: (v: number | null) => string;
  /** Which direction is good, for sorting emphasis and colour. */
  better?: Better;
}

const PAGE = 50;

export function BreakdownTable({
  rows,
  firstColumn,
  columns,
  showChange,
  onSelect,
  searchPlaceholder,
  empty,
  testId,
  renderLabel,
}: {
  rows: PlaybackBreakdownRow[];
  firstColumn: string;
  columns: MetricColumn[];
  showChange: boolean;
  onSelect?: (row: PlaybackBreakdownRow) => void;
  searchPlaceholder: string;
  empty: ReactNode;
  testId?: string;
  renderLabel?: (row: PlaybackBreakdownRow) => ReactNode;
}) {
  const { t } = useTranslation(["analytics", "common"]);
  const { locale } = useFormat();
  const [sortKey, setSortKey] = useState<string>("plays");
  const [desc, setDesc] = useState(true);
  const [search, setSearch] = useState("");
  const [limit, setLimit] = useState(PAGE);

  const sorted = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const filtered = needle
      ? rows.filter(
          (r) =>
            r.label.toLowerCase().includes(needle) ||
            r.key.toLowerCase().includes(needle) ||
            (r.sublabel ?? "").toLowerCase().includes(needle)
        )
      : rows;
    const value = (r: PlaybackBreakdownRow): number | string => {
      if (sortKey === "__label") return r.label.toLowerCase();
      if (sortKey === "__change") {
        return r.previousPlays > 0
          ? (r.metrics.plays - r.previousPlays) / r.previousPlays
          : Number.POSITIVE_INFINITY;
      }
      const v = r.metrics[sortKey as keyof PlaybackMetrics];
      // Unknown sorts last in either direction: an empty cell is not a zero.
      return v === null ? (desc ? -Infinity : Infinity) : v;
    };
    return [...filtered].sort((a, b) => {
      const va = value(a);
      const vb = value(b);
      const cmp =
        typeof va === "string" && typeof vb === "string"
          ? va.localeCompare(vb, locale)
          : (va as number) - (vb as number);
      return desc ? -cmp : cmp;
    });
  }, [rows, sortKey, desc, search, locale]);

  const sortBy = (key: string) => {
    if (key === sortKey) setDesc(!desc);
    else {
      setSortKey(key);
      setDesc(key !== "__label");
    }
  };

  return (
    <Box data-testid={testId}>
      <TextField
        size="small"
        placeholder={searchPlaceholder}
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setLimit(PAGE);
        }}
        sx={{ mb: 1.5, minWidth: 280 }}
        inputProps={{ "aria-label": searchPlaceholder }}
        data-testid={testId ? `${testId}-search` : undefined}
      />
      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {empty}
        </Typography>
      ) : (
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 220 }}>
                  <TableSortLabel
                    active={sortKey === "__label"}
                    direction={sortKey === "__label" && desc ? "desc" : "asc"}
                    onClick={() => sortBy("__label")}
                  >
                    {firstColumn}
                  </TableSortLabel>
                </TableCell>
                {columns.map((c, i) => (
                  <Fragment key={c.key}>
                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                      <TableSortLabel
                        active={sortKey === c.key}
                        direction={sortKey === c.key && desc ? "desc" : "asc"}
                        onClick={() => sortBy(c.key)}
                      >
                        {c.label}
                      </TableSortLabel>
                    </TableCell>
                    {showChange && i === 0 && c.key === "plays" && (
                      <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                        <TableSortLabel
                          active={sortKey === "__change"}
                          direction={
                            sortKey === "__change" && desc ? "desc" : "asc"
                          }
                          onClick={() => sortBy("__change")}
                        >
                          {t("common:labels.vsBefore")}
                        </TableSortLabel>
                      </TableCell>
                    )}
                  </Fragment>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.slice(0, limit).map((r) => (
                <TableRow
                  key={r.key || "∅"}
                  hover={Boolean(onSelect)}
                  onClick={onSelect ? () => onSelect(r) : undefined}
                  sx={{ cursor: onSelect ? "pointer" : "default" }}
                  data-testid={testId ? `${testId}-row-${r.key}` : undefined}
                >
                  <TableCell>
                    {renderLabel ? (
                      renderLabel(r)
                    ) : (
                      <Box>
                        <Typography
                          variant="bodySm"
                          sx={{
                            fontWeight: "fontWeightMedium",
                            color: tokens.ink,
                          }}
                        >
                          {r.label}
                        </Typography>
                        {r.sublabel && (
                          <Typography
                            variant="micro"
                            sx={{ color: tokens.inkFaint }}
                          >
                            {r.sublabel}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </TableCell>
                  {columns.map((c, i) => (
                    <Fragment key={c.key}>
                      <TableCell
                        align="right"
                        sx={{
                          fontVariantNumeric: "tabular-nums",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {c.format(r.metrics[c.key])}
                      </TableCell>
                      {showChange && i === 0 && c.key === "plays" && (
                        <TableCell align="right">
                          <Stack direction="row" justifyContent="flex-end">
                            <ChangeBadge
                              now={r.metrics.plays}
                              before={r.previousPlays}
                              better="up"
                            />
                          </Stack>
                        </TableCell>
                      )}
                    </Fragment>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {sorted.length > limit && (
        <Button
          size="small"
          onClick={() => setLimit(limit + PAGE)}
          sx={{ mt: 1 }}
          data-testid={testId ? `${testId}-more` : undefined}
        >
          {t("breakdownTable.showMore", {
            count: Math.min(PAGE, sorted.length - limit),
            remaining: sorted.length - limit,
          })}
        </Button>
      )}
    </Box>
  );
}
